import type {CreateExtensionArgument, CreateExtArgs, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {
    BuilderStyle,
    GlobalStyleDefinition,
    Pointer,
    CompRef,
    PossibleStyleForBuilderComponent
} from '@wix/document-services-types'
import {CSS_SHORTHAND_MAPPINGS} from '../builderComponents/shorthandMappings'
import {isEmpty, pick, set} from 'lodash'
import type {VariablesExtensionAPI} from '../variables/variables'
import {deepClone} from '@wix/wix-immutable-proxy'
import {DATA_TYPES, MASTER_PAGE_ID} from '../../constants/constants'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'

export interface CssStyleExtensionAPI extends ExtensionAPI {
    cssStyle: {
        getPublicCSSStyle(styleItem: BuilderStyle): BuilderStyle
        getUpdatedCSSStyleItemForRemove(existingStyle: BuilderStyle, paths: string[]): BuilderStyle | undefined
        getUpdatedCSSStyleItemForUpdate(
            existingStyle: BuilderStyle,
            partialStyle: Partial<BuilderStyle>,
            shouldRemoveRelatedProperties?: boolean
        ): BuilderStyle
        components: {
            getCSSStyleByPointer(pointer: Pointer): PossibleStyleForBuilderComponent
            getCSSStyleWithResolvedVariablesByPointer(pointer: Pointer): PossibleStyleForBuilderComponent
        }
    }
}

const createExtension = ({}: CreateExtensionArgument): Extension => {
    const createExtensionAPI = ({extensionAPI, dal, pointers}: CreateExtArgs): CssStyleExtensionAPI => {
        const {global, variables, variants, dataModel} = extensionAPI as VariablesExtensionAPI &
            VariantsExtensionAPI &
            DataModelExtensionAPI

        const removeExpandedProperties = (
            existingProperties: BuilderStyle['cssCustomProperties'],
            propertiesToRemoveExpansions: string[]
        ) => {
            const propertiesToRemove = new Set<string>()
            const toProcess = propertiesToRemoveExpansions.slice()
            const processed = new Set<string>()

            while (toProcess.length > 0) {
                const currentProperty = toProcess.pop()!

                // Skip if already processed
                if (processed.has(currentProperty)) {
                    continue
                }

                processed.add(currentProperty)

                // Look up expansions for this property
                const expansions = CSS_SHORTHAND_MAPPINGS[currentProperty]
                if (expansions) {
                    // Add all expansion property names to both removal set and processing array
                    expansions.forEach(({propertyName}) => {
                        if (!processed.has(propertyName)) {
                            propertiesToRemove.add(propertyName)
                            toProcess.push(propertyName)
                        }
                    })
                }
            }

            // Remove all collected cssProperties from styleItem
            propertiesToRemove.forEach(propertyName => {
                delete existingProperties![propertyName]
            })
        }

        //If there is no existing style, you must still pass an object with {type: TheStyleType} to this function
        const getUpdatedCSSStyleItemForUpdate = (
            existingStyle: BuilderStyle,
            partialStyle: Partial<BuilderStyle>,
            shouldRemoveRelatedProperties: boolean = true
        ) => {
            const {cssProperties = {}, cssCustomProperties = {}, type} = deepClone(existingStyle)

            delete cssProperties.variableConnections
            delete cssCustomProperties.variableConnections

            // Handle cssProperties update
            if (partialStyle.cssProperties) {
                if (shouldRemoveRelatedProperties && Object.keys(cssProperties).length > 0) {
                    removeExpandedProperties(cssProperties, Object.keys(partialStyle.cssProperties))
                }
                Object.assign(cssProperties, partialStyle.cssProperties)
            }

            // Handle cssCustomProperties update
            if (partialStyle.cssCustomProperties) {
                Object.assign(cssCustomProperties, partialStyle.cssCustomProperties)
            }

            // Handle spx update
            const newStyleItem: any = {
                ...existingStyle,
                type
            }
            //only assign if we had an update:
            if (partialStyle.cssProperties) {
                newStyleItem.cssProperties = cssProperties
            }

            if (partialStyle.cssCustomProperties) {
                newStyleItem.cssCustomProperties = cssCustomProperties
            }

            if (partialStyle.spx !== undefined) {
                newStyleItem.spx = partialStyle.spx
            }

            return variables.updateCssVariableConnections(newStyleItem)
        }

        const getUpdatedCSSStyleItemForRemove = (
            styleItem: BuilderStyle,
            paths: string[]
        ): BuilderStyle | undefined => {
            delete styleItem.cssProperties?.variableConnections
            delete styleItem.cssCustomProperties?.variableConnections
            const pathHandlers: Record<string, (key?: string) => boolean> = {
                cssProperties: (key?: string) => {
                    if (key) {
                        if (styleItem.cssProperties?.[key]) {
                            delete styleItem.cssProperties[key]
                            return true
                        }
                        return false
                    }
                    // @ts-ignore
                    styleItem.cssProperties = null
                    return true
                },
                cssCustomProperties: (key?: string) => {
                    if (key) {
                        if (styleItem.cssCustomProperties?.[key]) {
                            delete styleItem.cssCustomProperties[key]
                            return true
                        }
                        return false
                    }
                    // @ts-ignore
                    styleItem.cssCustomProperties = null
                    return true
                },
                spx: () => {
                    if (styleItem.spx) {
                        // @ts-ignore
                        styleItem.spx = null
                        return true
                    }
                    return false
                }
            }

            const removePath = (path: string): boolean => {
                const [property, key] = path.split('.', 2)
                return pathHandlers[property]?.(key) || false
            }

            const hasChanges = paths.map(removePath).some(Boolean)

            if (hasChanges) {
                // Clean up empty objects - dataModel will remove null properties on update
                if (isEmpty(styleItem.cssCustomProperties)) {
                    // @ts-ignore
                    styleItem.cssCustomProperties = null
                }
                if (isEmpty(styleItem.cssProperties)) {
                    // @ts-ignore
                    styleItem.cssProperties = null
                }

                return variables.updateCssVariableConnections(styleItem)
            }
        }

        const getPublicCSSStyle = (styleItem: BuilderStyle): BuilderStyle => {
            delete styleItem?.cssProperties?.variableConnections
            delete styleItem?.cssCustomProperties?.variableConnections
            return pick(styleItem, ['cssProperties', 'cssCustomProperties', 'customCss', 'spx', 'type'])
        }

        //TODO: remove duplication
        const isGlobalStyle = (pointerOrId: Pointer | string): boolean => {
            const globalStylePointer =
                typeof pointerOrId === 'string' ? pointers.data.getThemeItem(pointerOrId, MASTER_PAGE_ID) : pointerOrId

            return dal.get(globalStylePointer)?.type === 'GlobalStyle'
        }

        const getGlobalStyle = (pointer: Pointer): BuilderStyle | undefined => {
            const globalStyleItem = variants.getItemConsideringVariantsForPointer(pointer, DATA_TYPES.theme, 'value')
            if (globalStyleItem) {
                return getPublicCSSStyle(globalStyleItem)
            }
        }

        const getCSSStyleWithResolvedVariablesByPointer = (pointer: CompRef): BuilderStyle | undefined => {
            const dataPointer = variants.getComponentDataPointerConsideringVariants(pointer, DATA_TYPES.theme)
            if (!dataPointer) {
                return
            }
            const styleItem = isGlobalStyle(dataPointer) ? getGlobalStyle(dataPointer) : dal.get(dataPointer)
            if (styleItem) {
                const resolvedStyleItem = deepClone(styleItem)
                resolvedStyleItem.variableConnections?.forEach(
                    ({path, variableId}: {path: string; variableId: string}) => {
                        const variableDataItem = dataModel.getItem(variableId, DATA_TYPES.variables)
                        const {cssVarName} = variableDataItem
                        const variableData = global.variables.getVariableDataByCssVarName(cssVarName)
                        if (variableData) {
                            set(resolvedStyleItem, path.split('/'), variableData.value)
                        }
                    }
                )

                return getPublicCSSStyle(resolvedStyleItem)
            }
        }

        const getCSSStyleByPointer = (pointer: CompRef): BuilderStyle | GlobalStyleDefinition | undefined => {
            const dataPointer = variants.getComponentDataPointerConsideringVariants(pointer, DATA_TYPES.theme)
            if (!dataPointer) {
                return
            }
            if (isGlobalStyle(dataPointer)) {
                const globalStyleDefinition = dal.get(dataPointer)
                return pick(globalStyleDefinition, ['name', 'id', 'styleType', 'innerElementStyles', 'type'])
            }
            const styleItem = dataModel.getItem(dataPointer.id, DATA_TYPES.theme)
            if (styleItem) {
                return getPublicCSSStyle(styleItem)
            }
        }

        return {
            cssStyle: {
                getPublicCSSStyle,
                getUpdatedCSSStyleItemForRemove,
                getUpdatedCSSStyleItemForUpdate,
                components: {
                    getCSSStyleByPointer,
                    getCSSStyleWithResolvedVariablesByPointer
                }
            }
        }
    }

    return {
        name: 'cssStyle',
        dependencies: new Set(['variables']),
        createExtensionAPI
    }
}

export {createExtension}
